import React, { useState, useEffect } from "react";
import { FormControl, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { toast } from "react-toastify";
import ReportTable from "./ReportTable";
import { axios_tenant } from "../../../../axios";
import { downloadReportXlsx, isReportExportError } from "./reportExportApi";
import { KOT_DISPLAY_DATE_FORMAT, formatKotDate, parseKotStoredDate, toKotStorageDate } from "../utils/dateHelpers";

const REPORT_ENDPOINT = "/kot/reports/inventor";

const InventorDetailsReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [clientFilter, setClientFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Shared by the list fetch and the export so the two can never drift apart - the download
  // has to reflect exactly the filters that produced what is on screen.
  const filters = {
    clientName: clientFilter,
    country: countryFilter,
    fromDate,
    toDate,
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios_tenant.get(REPORT_ENDPOINT, { params: filters });
      // Map to include unique ID for DataGrid
      setData((response?.data || []).map((row, index) => ({ id: index, ...row })));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientFilter, countryFilter, fromDate, toDate]);

  const handleDownload = async (exportFields) => {
    try {
      setLoading(true);
      await downloadReportXlsx(REPORT_ENDPOINT, filters, exportFields, "inventor_report.xlsx");
    } catch (error) {
      if (isReportExportError(error)) toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "clientName", headerName: "Client Name", width: 150 },
    { field: "clientNumber", headerName: "Client Number", width: 150 },
    { field: "inventorName", headerName: "Legal Name", width: 200 },
    { field: "nationality", headerName: "NAT", width: 100 },
    { field: "citizenship", headerName: "CIT", width: 100 },
    { field: "city", headerName: "City", width: 150 },
    { field: "state", headerName: "State", width: 150 },
    { field: "country", headerName: "Country", width: 150 },
    {
      field: "verifiedDate",
      headerName: "Verified On",
      width: 150,
      renderCell: (params) => <span>{formatKotDate(params.row.verifiedDate)}</span>,
    },
  ];

  // Derive unique clients and countries for dropdowns from data?
  // Ideally backend provides unique lists, but we can do simple unique extract if we have full data, 
  // or just text inputs. The mockup uses Dropdowns. If data is paginated, dropdown only has current page.
  // We'll use text field for Client Name or custom logic if it's dynamic. Let's assume text field or use simple unique mapping.
  // We'll use TextField for simplicity and robustness.
  
  const filtersNode = (
    <>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <TextField
          size="small"
          label="Client Name"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
        />
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <TextField
          size="small"
          label="Country"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
        />
      </FormControl>
      <FormControl size="small">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="From"
            value={parseKotStoredDate(fromDate)}
            onChange={(newValue) => setFromDate(newValue ? toKotStorageDate(newValue) : "")}
            inputFormat={KOT_DISPLAY_DATE_FORMAT}
            renderInput={(pickerParams) => <TextField {...pickerParams} size="small" onKeyDown={(e) => {
              if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                e.preventDefault();
              }
            }} />}
          />
        </LocalizationProvider>
      </FormControl>
      <FormControl size="small">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="To"
            value={parseKotStoredDate(toDate)}
            onChange={(newValue) => setToDate(newValue ? toKotStorageDate(newValue) : "")}
            inputFormat={KOT_DISPLAY_DATE_FORMAT}
            renderInput={(pickerParams) => <TextField {...pickerParams} size="small" onKeyDown={(e) => {
              if (e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "Enter") {
                e.preventDefault();
              }
            }} />}
          />
        </LocalizationProvider>
      </FormControl>
    </>
  );

  return (
    <ReportTable
      title="Reports"
      columns={columns}
      data={data}
      loading={loading}
      filtersNode={filtersNode}
      onDownload={handleDownload}
    />
  );
};

export default InventorDetailsReport;
